// /admin/settings — Richie's tunables console.
//
// Four tabs:
//   • Numbers  — founding cohort cap + every sweeper day-window
//   • SLA      — dispute ack thresholds (hours)
//   • Pricing  — displayed AUD prices
//   • Banner   — global announcement banner (singleton row)
//
// API contract (worker):
//   GET    /api/admin/settings                   → { settings, banner, registry, recentChanges }
//   PUT    /api/admin/settings/:key              → body { value }
//   PUT    /api/admin/settings/banner            → body { message, audience, visibleFromMs, ..., enabled }
//   DELETE /api/admin/settings/banner            → disable
//
// The "Recent changes" panel reads from the GET / response and renders a
// one-click revert button per row using `payload.from`. Mirrors the launch
// page's recentChanges affordance.

import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  PLATFORM_SETTING_CATEGORY_LABELS,
  type PlatformSettingCategory,
} from '@wwaave/shared/platform-settings';
import {
  ART_FORM_GROUP_LABELS,
  ART_FORM_GROUPS,
  type ArtFormGroup,
  VENUE_TYPE_GROUP_LABELS,
  VENUE_TYPE_GROUPS,
  type VenueTypeGroup,
} from '@wwaave/shared/vocab';
import { useMemo, useState } from 'react';

import { BrandingPanel } from '../../components/admin-settings/BrandingPanel';
import { CmsPanel } from '../../components/admin-settings/CmsPanel';
import { IntegrationsPanel } from '../../components/admin-settings/IntegrationsPanel';
import { NotificationCopyPanel } from '../../components/admin-settings/NotificationCopyPanel';
import { AdminMfaRequired } from '../../components/admin-shell/AdminMfaRequired';
import { AdminShell } from '../../components/admin-shell/AdminShell';
import { showToast } from '../../lib/toast';
import {
  adminDelete,
  adminGet,
  adminPatch,
  adminPost,
  adminPut,
  isMfaEnrollmentRequired,
} from './_api';

// ─── API response shapes ──────────────────────────────────────────────────

type RegistryEntry = {
  key: string;
  label: string;
  description: string;
  category: PlatformSettingCategory;
  valueType: 'number' | 'string' | 'boolean' | 'json';
  default: number | string | boolean;
  min?: number;
  max?: number;
  unit?: string;
  policyNote: string;
};

type SettingRow = {
  key: string;
  value: number | string | boolean;
  updatedAt: string;
  updatedByUserId: string | null;
};

type BannerShape = {
  message: string;
  audience: 'all' | 'signed_in' | 'artists' | 'hosts' | 'founding_cohort';
  visibleFromMs: number | null;
  visibleToMs: number | null;
  dismissible: boolean;
  enabled: boolean;
  dismissId: string;
};

type RecentChange = {
  id: string;
  action: string;
  payload: Record<string, unknown> | null;
  source: string | null;
  targetKind: string | null;
  targetId: string | null;
  createdAt: string;
  actorDisplayName: string | null;
};

type SettingsResponse = {
  settings: SettingRow[];
  banner: BannerShape | null;
  registry: RegistryEntry[];
  recentChanges: RecentChange[];
};

// ─── Route ────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/admin/settings')({
  component: AdminSettingsPage,
});

function AdminSettingsPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => adminGet<SettingsResponse>('/api/admin/settings'),
  });

  // The admin two-factor gate 403s this load before it ever reaches the handler.
  // That isn't an outage, and it takes the whole page down with it (the CMS tab
  // included), so it gets its own actionable state rather than the generic alert.
  const mfaBlocked = isMfaEnrollmentRequired(error);

  return (
    <AdminShell breadcrumb="Platform settings">
      <Stack gap="lg">
        {mfaBlocked ? (
          <AdminMfaRequired />
        ) : (
          <>
            <Box>
              <Title order={2}>Platform settings</Title>
              <Text c="dimmed" mt={4}>
                Tunable values, SLAs, displayed pricing, and the site-wide banner. Every
                change is audited; revert from "Recent changes" below each tab.
              </Text>
            </Box>

            {isLoading ? <Loader /> : null}
            {isError ? (
              <Alert color="red" title="Could not load settings">
                {error instanceof Error && error.message !== ''
                  ? error.message
                  : 'Try refreshing. If this persists, the worker may be down.'}
              </Alert>
            ) : null}

            {data !== undefined ? <SettingsTabs data={data} /> : null}
          </>
        )}
      </Stack>
    </AdminShell>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────────

function SettingsTabs({ data }: { data: SettingsResponse }) {
  const settingsByKey = useMemo(() => {
    const m = new Map<string, SettingRow>();
    for (const r of data.settings) m.set(r.key, r);
    return m;
  }, [data.settings]);

  return (
    <Tabs defaultValue="branding" keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="branding">{PLATFORM_SETTING_CATEGORY_LABELS.branding}</Tabs.Tab>
        <Tabs.Tab value="numbers">{PLATFORM_SETTING_CATEGORY_LABELS.numbers}</Tabs.Tab>
        <Tabs.Tab value="sla">{PLATFORM_SETTING_CATEGORY_LABELS.sla}</Tabs.Tab>
        <Tabs.Tab value="pricing">{PLATFORM_SETTING_CATEGORY_LABELS.pricing}</Tabs.Tab>
        <Tabs.Tab value="banner">Banner</Tabs.Tab>
        <Tabs.Tab value="integrations">Integrations</Tabs.Tab>
        <Tabs.Tab value="vocab">Vocab</Tabs.Tab>
        <Tabs.Tab value="cms">CMS</Tabs.Tab>
        <Tabs.Tab value="notifications">Notifications</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="branding" pt="lg">
        <BrandingPanel settingsByKey={settingsByKey} />
      </Tabs.Panel>
      <Tabs.Panel value="numbers" pt="lg">
        <CategoryPanel
          category="numbers"
          registry={data.registry}
          settingsByKey={settingsByKey}
          recentChanges={data.recentChanges}
        />
      </Tabs.Panel>
      <Tabs.Panel value="sla" pt="lg">
        <CategoryPanel
          category="sla"
          registry={data.registry}
          settingsByKey={settingsByKey}
          recentChanges={data.recentChanges}
        />
      </Tabs.Panel>
      <Tabs.Panel value="pricing" pt="lg">
        <CategoryPanel
          category="pricing"
          registry={data.registry}
          settingsByKey={settingsByKey}
          recentChanges={data.recentChanges}
        />
      </Tabs.Panel>
      <Tabs.Panel value="banner" pt="lg">
        <BannerPanel banner={data.banner} recentChanges={data.recentChanges} />
      </Tabs.Panel>
      <Tabs.Panel value="integrations" pt="lg">
        <IntegrationsPanel />
      </Tabs.Panel>
      <Tabs.Panel value="vocab" pt="lg">
        <VocabPanel />
      </Tabs.Panel>
      <Tabs.Panel value="cms" pt="lg">
        <CmsPanel />
      </Tabs.Panel>
      <Tabs.Panel value="notifications" pt="lg">
        <NotificationCopyPanel />
      </Tabs.Panel>
    </Tabs>
  );
}

// ─── Vocab panel ──────────────────────────────────────────────────────────

type VocabEntity = 'art_forms' | 'venue_types' | 'vibe_tags' | 'amenities' | 'countries';

type VocabEntry = {
  id: string;
  slug: string;
  name: string;
  enabled: boolean;
  displayOrder: number;
  /** art_forms carry an ArtFormGroup, venue_types a VenueTypeGroup. */
  group?: ArtFormGroup | VenueTypeGroup;
  scope?: 'sound_tech' | 'sleeping' | 'food' | 'drink' | 'facilities';
};

/** Entities whose rows carry a `group`, and the options their Add form offers. */
const GROUP_OPTIONS: Partial<Record<VocabEntity, { value: string; label: string }[]>> = {
  art_forms: ART_FORM_GROUPS.map((g) => ({ value: g, label: ART_FORM_GROUP_LABELS[g] })),
  venue_types: VENUE_TYPE_GROUPS.map((g) => ({ value: g, label: VENUE_TYPE_GROUP_LABELS[g] })),
};

function hasGroup(entity: VocabEntity): boolean {
  return GROUP_OPTIONS[entity] !== undefined;
}

function groupLabel(group: string | undefined): string {
  if (group === undefined) return '–';
  return (
    { ...ART_FORM_GROUP_LABELS, ...VENUE_TYPE_GROUP_LABELS } as Record<string, string | undefined>
  )[group] ?? group;
}

const VOCAB_TABS: ReadonlyArray<{ value: VocabEntity; label: string }> = [
  { value: 'art_forms', label: 'Art forms' },
  { value: 'venue_types', label: 'Venue types' },
  { value: 'vibe_tags', label: 'Vibe tags' },
  { value: 'amenities', label: 'Amenities' },
  { value: 'countries', label: 'Countries' },
];

function VocabPanel() {
  const [activeEntity, setActiveEntity] = useState<VocabEntity>('art_forms');
  return (
    <Stack gap="md">
      <Tabs value={activeEntity} onChange={(v) => setActiveEntity((v ?? 'art_forms') as VocabEntity)}>
        <Tabs.List>
          {VOCAB_TABS.map((t) => (
            <Tabs.Tab key={t.value} value={t.value}>
              {t.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
      <VocabEntityPanel entity={activeEntity} />
    </Stack>
  );
}

function VocabEntityPanel({ entity }: { entity: VocabEntity }) {
  const qc = useQueryClient();
  const [showDisabled, setShowDisabled] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'vocab', entity],
    queryFn: () => adminGet<{ items: VocabEntry[] }>(`/api/admin/vocab/${entity}`),
  });

  const rows = useMemo(() => {
    const items = query.data?.items ?? [];
    return showDisabled ? items : items.filter((r) => r.enabled);
  }, [query.data, showDisabled]);

  const allowAdd = entity !== 'countries';

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Group gap="md">
          <Switch
            label="Show disabled"
            checked={showDisabled}
            onChange={(e) => setShowDisabled(e.currentTarget.checked)}
          />
          <Text size="sm" c="dimmed">
            {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
          </Text>
        </Group>
        {allowAdd ? (
          <Button size="xs" onClick={() => setAddOpen(true)}>
            + Add slug
          </Button>
        ) : null}
      </Group>

      {query.isLoading ? (
        <Loader size="sm" />
      ) : query.isError ? (
        <Alert color="red">Could not load {entity}.</Alert>
      ) : (
        <Card withBorder padding="md" radius="md">
          <Table striped withTableBorder={false} verticalSpacing="xs" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Slug</Table.Th>
                <Table.Th>Name</Table.Th>
                {hasGroup(entity) ? <Table.Th>Group</Table.Th> : null}
                {entity === 'amenities' ? <Table.Th>Scope</Table.Th> : null}
                <Table.Th style={{ width: 100 }}>Order</Table.Th>
                <Table.Th style={{ width: 100 }}>Enabled</Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <VocabRow
                  key={r.id}
                  entity={entity}
                  row={r}
                  onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'vocab', entity] })}
                />
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <Modal opened={addOpen} onClose={() => setAddOpen(false)} title={`Add ${entity}`}>
        <AddVocabForm
          entity={entity}
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            setAddOpen(false);
            void qc.invalidateQueries({ queryKey: ['admin', 'vocab', entity] });
          }}
        />
      </Modal>
    </Stack>
  );
}

function VocabRow({
  entity,
  row,
  onSaved,
}: {
  entity: VocabEntity;
  row: VocabEntry;
  onSaved: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [displayOrder, setDisplayOrder] = useState(row.displayOrder);
  const [enabled, setEnabled] = useState(row.enabled);

  const save = useMutation({
    mutationFn: async () => {
      return adminPatch<{ ok: true }>(`/api/admin/vocab/${entity}/${row.id}`, {
        name,
        displayOrder,
        enabled,
      });
    },
    onSuccess: () => {
      showToast({ kind: 'success', body: `Saved ${row.slug}.` });
      onSaved();
    },
    onError: (err: unknown) => {
      showToast({ kind: 'error', body: err instanceof Error ? err.message : 'Save failed.' });
    },
  });

  const dirty = name !== row.name || displayOrder !== row.displayOrder || enabled !== row.enabled;

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {row.slug}
        </Text>
      </Table.Td>
      <Table.Td>
        <TextInput value={name} onChange={(e) => setName(e.currentTarget.value)} size="xs" />
      </Table.Td>
      {hasGroup(entity) ? (
        <Table.Td>
          <Text size="xs" c="dimmed">
            {groupLabel(row.group)}
          </Text>
        </Table.Td>
      ) : null}
      {entity === 'amenities' ? (
        <Table.Td>
          <Text size="xs" c="dimmed">
            {row.scope ?? '–'}
          </Text>
        </Table.Td>
      ) : null}
      <Table.Td>
        <NumberInput
          value={displayOrder}
          onChange={(v) => setDisplayOrder(typeof v === 'number' ? v : Number(v ?? 0))}
          min={0}
          max={9999}
          size="xs"
          w={80}
        />
      </Table.Td>
      <Table.Td>
        <Switch checked={enabled} onChange={(e) => setEnabled(e.currentTarget.checked)} />
      </Table.Td>
      <Table.Td>
        <Button
          size="xs"
          variant={dirty ? 'filled' : 'subtle'}
          disabled={!dirty || save.isPending}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </Table.Td>
    </Table.Tr>
  );
}

function AddVocabForm({
  entity,
  onClose,
  onCreated,
}: {
  entity: VocabEntity;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [displayOrder, setDisplayOrder] = useState(999);
  const groupOptions = GROUP_OPTIONS[entity];
  const defaultGroup = (groupOptions?.[0]?.value ?? 'performance') as ArtFormGroup | VenueTypeGroup;
  const [group, setGroup] = useState<ArtFormGroup | VenueTypeGroup>(defaultGroup);
  const [scope, setScope] = useState<VocabEntry['scope']>('sound_tech');

  const create = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { slug, name, displayOrder };
      if (groupOptions !== undefined) body['group'] = group;
      if (entity === 'amenities') body['scope'] = scope;
      return adminPost<{ ok: true; entry: VocabEntry }>(`/api/admin/vocab/${entity}`, body);
    },
    onSuccess: () => {
      showToast({ kind: 'success', body: `Created ${slug}.` });
      onCreated();
    },
    onError: (err: unknown) => {
      showToast({ kind: 'error', body: err instanceof Error ? err.message : 'Create failed.' });
    },
  });

  const slugRegex = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
  const slugValid = slugRegex.test(slug) && slug.length >= 2 && slug.length <= 80;
  const nameValid = name.trim().length > 0 && name.length <= 120;

  return (
    <Stack gap="sm">
      <TextInput
        label="Slug"
        description="Lowercase letters, digits, hyphens. Immutable once created."
        value={slug}
        onChange={(e) => setSlug(e.currentTarget.value.toLowerCase())}
        // This field only renders inside the "Add {entity}" Modal (see below);
        // moving focus to the first field when a dialog opens is the WAI-ARIA
        // APG dialog pattern, not an unexpected/disorienting jump on page load.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />
      <TextInput
        label="Display name"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />
      <NumberInput
        label="Display order"
        value={displayOrder}
        onChange={(v) => setDisplayOrder(typeof v === 'number' ? v : Number(v ?? 999))}
        min={0}
        max={9999}
      />
      {groupOptions !== undefined ? (
        <Select
          label="Group"
          data={groupOptions}
          value={group}
          onChange={(v) => setGroup((v ?? defaultGroup) as ArtFormGroup | VenueTypeGroup)}
          allowDeselect={false}
        />
      ) : null}
      {entity === 'amenities' ? (
        <Select
          label="Scope"
          data={[
            { value: 'sound_tech', label: 'Sound tech' },
            { value: 'sleeping', label: 'Sleeping' },
            { value: 'food', label: 'Food' },
            { value: 'drink', label: 'Drink' },
            { value: 'facilities', label: 'Facilities' },
          ]}
          value={scope ?? 'sound_tech'}
          onChange={(v) => setScope((v ?? 'sound_tech') as VocabEntry['scope'])}
          allowDeselect={false}
        />
      ) : null}
      <Group justify="flex-end" gap="sm" mt="sm">
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={!slugValid || !nameValid || create.isPending}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          Create
        </Button>
      </Group>
    </Stack>
  );
}

// ─── Category panel (Numbers / SLA / Pricing) ─────────────────────────────

function CategoryPanel({
  category,
  registry,
  settingsByKey,
  recentChanges,
}: {
  category: PlatformSettingCategory;
  registry: RegistryEntry[];
  settingsByKey: Map<string, SettingRow>;
  recentChanges: RecentChange[];
}) {
  const entries = useMemo(
    () => registry.filter((r) => r.category === category),
    [registry, category],
  );

  const filtered = useMemo(
    () =>
      recentChanges.filter(
        (r) =>
          r.source === 'platform-setting' &&
          r.targetId !== null &&
          entries.some((e) => e.key === r.targetId),
      ),
    [recentChanges, entries],
  );

  return (
    <Stack gap="lg">
      <Stack gap="md">
        {entries.map((entry) => (
          <SettingRowCard
            key={entry.key}
            entry={entry}
            current={settingsByKey.get(entry.key)}
          />
        ))}
      </Stack>
      <RecentChangesPanel changes={filtered} />
    </Stack>
  );
}

function SettingRowCard({
  entry,
  current,
}: {
  entry: RegistryEntry;
  current: SettingRow | undefined;
}) {
  const initial =
    current !== undefined ? current.value : entry.default;
  const [draft, setDraft] = useState<number | string | boolean>(initial);
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: async (value: number | string | boolean) => {
      return adminPut<{ ok: true; from: unknown; to: unknown }>(
        `/api/admin/settings/${entry.key}`,
        { value },
      );
    },
    onSuccess: async () => {
      showToast({ kind: 'success', body: `Saved “${entry.label}”.` });
      await qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
    onError: (err: unknown) => {
      showToast({
        kind: 'error',
        body: err instanceof Error ? err.message : 'Save failed.',
      });
    },
  });

  const dirty = draft !== initial;
  const isDefault = !current;

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box style={{ flex: 1 }}>
            <Group gap="xs" align="baseline">
              <Text fw={600}>{entry.label}</Text>
              {isDefault ? (
                <Badge variant="light" size="xs" color="gray">
                  default
                </Badge>
              ) : null}
            </Group>
            <Text size="sm" c="dimmed">
              {entry.description}
            </Text>
          </Box>
          <Group gap="xs" wrap="nowrap" align="center">
            {entry.valueType === 'number' ? (
              <NumberInput
                value={typeof draft === 'number' ? draft : Number(draft)}
                onChange={(v) =>
                  setDraft(typeof v === 'number' ? v : Number(v ?? entry.default))
                }
                {...(entry.min !== undefined ? { min: entry.min } : {})}
                {...(entry.max !== undefined ? { max: entry.max } : {})}
                w={140}
                step={1}
                clampBehavior="strict"
              />
            ) : entry.valueType === 'boolean' ? (
              <Switch
                checked={Boolean(draft)}
                onChange={(e) => setDraft(e.currentTarget.checked)}
              />
            ) : (
              <TextInput
                value={String(draft)}
                onChange={(e) => setDraft(e.currentTarget.value)}
                w={240}
              />
            )}
            {entry.unit !== undefined ? (
              <Text c="dimmed" size="sm">
                {entry.unit}
              </Text>
            ) : null}
            <Button
              size="xs"
              variant={dirty ? 'filled' : 'subtle'}
              disabled={!dirty || save.isPending}
              loading={save.isPending}
              onClick={() => save.mutate(draft)}
            >
              Save
            </Button>
          </Group>
        </Group>
        <Text size="xs" c="dimmed">
          {entry.policyNote}
          {current !== undefined ? (
            <>
              {' '}
              <Text component="span" c="ink.6">
                · Last updated {formatTime(current.updatedAt)}
              </Text>
            </>
          ) : null}
        </Text>
      </Stack>
    </Card>
  );
}

// ─── Banner panel ─────────────────────────────────────────────────────────

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All visitors' },
  { value: 'signed_in', label: 'Signed-in users' },
  { value: 'artists', label: 'Artists only' },
  { value: 'hosts', label: 'Hosts only' },
  { value: 'founding_cohort', label: 'Founding cohort' },
] as const;

function BannerPanel({
  banner,
  recentChanges,
}: {
  banner: BannerShape | null;
  recentChanges: RecentChange[];
}) {
  const initial = banner ?? {
    message: '',
    audience: 'all' as const,
    visibleFromMs: null,
    visibleToMs: null,
    dismissible: true,
    enabled: false,
    dismissId: '0',
  };

  const [draft, setDraft] = useState<BannerShape>(initial);
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: async () => {
      return adminPut<{ ok: true; banner: BannerShape }>('/api/admin/settings/banner', {
        message: draft.message,
        audience: draft.audience,
        visibleFromMs: draft.visibleFromMs,
        visibleToMs: draft.visibleToMs,
        dismissible: draft.dismissible,
        enabled: draft.enabled,
      });
    },
    onSuccess: async () => {
      showToast({ kind: 'success', body: 'Banner saved.' });
      await qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      await qc.invalidateQueries({ queryKey: ['platform-banner'] });
    },
    onError: (err: unknown) => {
      showToast({
        kind: 'error',
        body: err instanceof Error ? err.message : 'Save failed.',
      });
    },
  });

  const disable = useMutation({
    mutationFn: async () => {
      return adminDelete<{ ok: true; banner: BannerShape | null }>(
        '/api/admin/settings/banner',
      );
    },
    onSuccess: async () => {
      showToast({ kind: 'success', body: 'Banner disabled.' });
      await qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      await qc.invalidateQueries({ queryKey: ['platform-banner'] });
    },
    onError: (err: unknown) => {
      showToast({
        kind: 'error',
        body: err instanceof Error ? err.message : 'Disable failed.',
      });
    },
  });

  const filtered = useMemo(
    () => recentChanges.filter((r) => r.source === 'platform-banner'),
    [recentChanges],
  );

  return (
    <Stack gap="lg">
      <Card withBorder padding="md" radius="md">
        <Stack gap="md">
          <Title order={4}>Site-wide banner</Title>
          <Text size="sm" c="dimmed">
            One banner at a time. Audience and visibility window are enforced
            server-side; dismissible banners persist per-user via localStorage.
            Disabling keeps the row so editorial state survives "publish later".
          </Text>

          <Textarea
            label="Message"
            description="Plain text, shown across MarketingShell + AppShell."
            value={draft.message}
            onChange={(e) => setDraft({ ...draft, message: e.currentTarget.value })}
            autosize
            minRows={2}
            maxLength={2000}
          />

          <Group grow>
            <Select
              label="Audience"
              data={[...AUDIENCE_OPTIONS]}
              value={draft.audience}
              onChange={(v) =>
                setDraft({ ...draft, audience: (v ?? 'all') as BannerShape['audience'] })
              }
              allowDeselect={false}
            />
            <Switch
              label="Dismissible"
              description="Show a close button. Dismissals are per-user."
              checked={draft.dismissible}
              onChange={(e) => setDraft({ ...draft, dismissible: e.currentTarget.checked })}
              mt={26}
            />
          </Group>

          <Group grow>
            <DateInputMs
              label="Visible from"
              valueMs={draft.visibleFromMs}
              onChangeMs={(ms) => setDraft({ ...draft, visibleFromMs: ms })}
            />
            <DateInputMs
              label="Visible until"
              valueMs={draft.visibleToMs}
              onChangeMs={(ms) => setDraft({ ...draft, visibleToMs: ms })}
            />
          </Group>

          <Switch
            label="Enabled"
            description="When off, no one sees the banner regardless of the window."
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.currentTarget.checked })}
          />

          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              color="red"
              disabled={banner === null || !banner.enabled || disable.isPending}
              loading={disable.isPending}
              onClick={() => disable.mutate()}
            >
              Disable
            </Button>
            <Button
              loading={save.isPending}
              disabled={draft.message.trim().length === 0}
              onClick={() => save.mutate()}
            >
              Save banner
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="md" radius="md">
        <Stack gap="xs">
          <Title order={5}>Preview</Title>
          <BannerPreview draft={draft} />
        </Stack>
      </Card>

      <RecentChangesPanel changes={filtered} />
    </Stack>
  );
}

function BannerPreview({ draft }: { draft: BannerShape }) {
  if (draft.message.trim().length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Empty preview. Type a message above.
      </Text>
    );
  }
  return (
    <Box
      style={{
        background:
          'linear-gradient(90deg, var(--mantine-color-sun-0), var(--mantine-color-brand-0))',
        borderBottom: '1px solid var(--mantine-color-sun-2)',
        paddingBlock: 10,
        paddingInline: 16,
        borderRadius: 6,
      }}
    >
      <Group justify="center" gap="md" wrap="wrap" align="center">
        <Text size="sm" c="ink.8">
          {draft.message}
        </Text>
        {draft.dismissible ? (
          <Text size="xs" c="dimmed">
            (×)
          </Text>
        ) : null}
      </Group>
    </Box>
  );
}

// ─── Recent changes ──────────────────────────────────────────────────────

function RecentChangesPanel({ changes }: { changes: RecentChange[] }) {
  const qc = useQueryClient();

  const revert = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      return adminPut<{ ok: true }>(`/api/admin/settings/${key}`, { value });
    },
    onSuccess: async () => {
      showToast({ kind: 'success', body: 'Reverted.' });
      await qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
    onError: (err: unknown) => {
      showToast({
        kind: 'error',
        body: err instanceof Error ? err.message : 'Revert failed.',
      });
    },
  });

  if (changes.length === 0) {
    return (
      <Card withBorder padding="md" radius="md">
        <Text size="sm" c="dimmed">
          No recent changes.
        </Text>
      </Card>
    );
  }

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="xs">
        <Title order={5}>Recent changes</Title>
        {changes.map((c) => {
          const from = (c.payload as { from?: unknown } | null)?.from;
          const to = (c.payload as { to?: unknown } | null)?.to;
          const isSetting = c.source === 'platform-setting' && c.targetId !== null;
          return (
            <Group key={c.id} justify="space-between" align="center" wrap="nowrap">
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" lineClamp={1}>
                  <strong>{c.targetId ?? c.action}</strong> ·{' '}
                  <Text component="span" c="dimmed">
                    {String(from)} → {String(to)}
                  </Text>
                </Text>
                <Text size="xs" c="dimmed">
                  {formatTime(c.createdAt)}
                  {c.actorDisplayName !== null ? ` · ${c.actorDisplayName}` : ''}
                </Text>
              </Box>
              {isSetting && from !== undefined ? (
                <Anchor
                  component="button"
                  type="button"
                  size="xs"
                  onClick={() => revert.mutate({ key: c.targetId!, value: from })}
                >
                  Revert
                </Anchor>
              ) : null}
            </Group>
          );
        })}
      </Stack>
    </Card>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function DateInputMs({
  label,
  valueMs,
  onChangeMs,
}: {
  label: string;
  valueMs: number | null;
  onChangeMs: (ms: number | null) => void;
}) {
  // Mantine's DatePicker types are stricter than we need here; the simple
  // datetime-local input is plenty for setting a banner window. Empty input
  // means "open-ended".
  const value =
    valueMs === null
      ? ''
      : new Date(valueMs).toISOString().slice(0, 16); // 'YYYY-MM-DDTHH:mm'

  return (
    <TextInput
      label={label}
      placeholder="open-ended"
      value={value}
      type="datetime-local"
      onChange={(e) => {
        const v = e.currentTarget.value;
        if (v === '') {
          onChangeMs(null);
        } else {
          const ms = new Date(v).getTime();
          onChangeMs(Number.isFinite(ms) ? ms : null);
        }
      }}
    />
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
